/**
 * Diagnostic page — Inea's strategic diagnosis (Module 1 output).
 *
 * Shown right after onboarding completes, and reachable later from the
 * profile fiche. Renders the recommended positioning, strengths, blind
 * spots, per-platform tone, and a concrete 3-step action plan.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Loader2,
  Compass,
  Sparkles,
  TrendingUp,
  AlertTriangle,
  Radio,
  ListChecks,
  RefreshCw,
  ArrowRight,
  Palette,
} from 'lucide-react';
import { useDiagnostic } from '@/hooks/useDiagnostic';
export default function DiagnosticPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { diagnostic: d, status, error, isLoading, regenerate } = useDiagnostic();

  // Generation failed server-side — show a clear retry path instead of an
  // infinite spinner. The regenerate mutation already handles the retry.
  if (status === 'failed') {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-5 text-center">
        <AlertTriangle className="mt-6 text-amber-500" size={28} />
        <h2 className="mt-5 font-display text-[18px] font-800 tracking-[-0.02em]">
          {t('diagnostic.errorTitle')}
        </h2>
        <p className="mt-2 text-[13.5px] leading-[1.6] text-ink/60">
          {t('diagnostic.errorBody')}
        </p>
        {error?.reason && (
          <p className="mt-2 font-mono text-[10.5px] text-ink/40">{error.reason}</p>
        )}
        <button
          onClick={() => regenerate.mutate()}
          disabled={regenerate.isPending}
          className="hero-cta-light-primary mt-6 inline-flex items-center gap-2 disabled:opacity-50"
        >
          {regenerate.isPending ? (
            <Loader2 className="animate-spin" size={14} />
          ) : (
            <RefreshCw size={14} />
          )}
          Réessayer
        </button>
        <button
          onClick={() => navigate('/')}
          className="btn-ghost mt-3 px-4 py-2 text-[12.5px]"
        >
          {t('common.goToSpace')}
        </button>
      </div>
    );
  }

  // While the diagnostic is being generated server-side, useDiagnostic keeps
  // polling — show a friendly "Inea is working" state, not an empty screen.
  if (isLoading || !d) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-5 text-center">
        <Loader2 className="mt-6 animate-spin text-pink" size={26} />
        <h2 className="mt-5 font-display text-[18px] font-800 tracking-[-0.02em]">
          {t('diagnostic.buildingTitle')}
        </h2>
        <p className="mt-2 text-[13.5px] leading-[1.6] text-ink/60">
          {t('diagnostic.buildingBody')}
        </p>
        <button
          onClick={() => navigate('/')}
          className="btn-ghost mt-6 px-4 py-2 text-[12.5px]"
        >
          {t('common.goToSpace')}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[820px] px-5 py-10 sm:px-8">
      <header className="mb-7 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3.5">
          <div>
            <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">{t('diagnostic.title')}</p>
            <h1 className="font-display text-[clamp(20px,3vw,28px)] font-800 tracking-[-0.025em]">
              Votre plan de match
            </h1>
          </div>
        </div>
        <button
          onClick={() => regenerate.mutate()}
          disabled={regenerate.isPending}
          className="btn-ghost px-3.5 py-2 text-[12.5px] disabled:opacity-60"
        >
          {regenerate.isPending ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <RefreshCw size={13} />
          )}
          <span className="hidden sm:inline">{t("diagnostic.regenerateShort")}</span>
        </button>
      </header>

      <div className="space-y-5">
        {/* Headline + positioning */}
        <Section delay={0}>
          <div className="card-hi relative overflow-hidden rounded-[26px] p-7 sm:p-8">
            <div
              className="pointer-events-none absolute -right-16 -top-20 h-60 w-60 rounded-full opacity-40 blur-3xl"
              style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }}
            />
            <p className="kicker text-violet">
              <Sparkles size={12} /> Lecture d'Inea
            </p>
            <h2 className="mt-2 font-display text-[clamp(18px,2.6vw,24px)] font-800 leading-[1.25] tracking-[-0.025em]">
              {d.headline}
            </h2>
            {d.recommended_positioning && (
              <div className="mt-5 rounded-2xl bg-gradient-to-br from-blue/[0.07] to-pink/[0.06] p-5">
                <div className="flex items-center gap-2">
                  <Compass size={15} className="text-blue" />
                  <p className="font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-ink/50">
                    {t('diagnostic.positioning')}
                  </p>
                </div>
                <p className="mt-1.5 font-display text-[18px] font-800 tracking-[-0.02em] text-grad">
                  {d.recommended_positioning}
                </p>
                {d.positioning_rationale && (
                  <p className="mt-2 text-[13.5px] leading-[1.6] text-ink/70">
                    {d.positioning_rationale}
                  </p>
                )}
              </div>
            )}
          </div>
        </Section>

        {/* Strengths + blind spots */}
        <div className="grid gap-5 sm:grid-cols-2">
          {Array.isArray(d.strengths) && d.strengths.length > 0 && (
            <Section delay={0.08}>
              <Card kicker="Vos atouts" title={t("diagnostic.strengthsTitle")} icon={<TrendingUp size={16} />}>
                <ul className="space-y-2">
                  {d.strengths.map((s, i) => (
                    <li key={i} className="flex gap-2.5 text-[13.5px] leading-[1.5] text-ink/80">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                      {s}
                    </li>
                  ))}
                </ul>
              </Card>
            </Section>
          )}
          {Array.isArray(d.blind_spots) && d.blind_spots.length > 0 && (
            <Section delay={0.14}>
              <Card
                kicker="Points de vigilance"
                title={t("diagnostic.watchTitle")}
                icon={<AlertTriangle size={16} />}
              >
                <ul className="space-y-2">
                  {d.blind_spots.map((s, i) => (
                    <li key={i} className="flex gap-2.5 text-[13.5px] leading-[1.5] text-ink/80">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                      {s}
                    </li>
                  ))}
                </ul>
              </Card>
            </Section>
          )}
        </div>

        {/* Platform tones */}
        {Array.isArray(d.platform_tones) && d.platform_tones.length > 0 && (
          <Section delay={0.2}>
            <Card kicker="Ton de communication" title={t("diagnostic.voiceTitle")} icon={<Radio size={16} />}>
              <div className="grid gap-3 sm:grid-cols-2">
                {d.platform_tones.map((pt, i) => (
                  <div key={i} className="rounded-2xl border border-ink/[0.07] p-4">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-[14px] font-800 text-ink">
                        {pt.platform}
                      </span>
                      <span className="rounded-full bg-violet/10 px-2 py-0.5 text-[10.5px] font-700 text-violet">
                        {pt.tone}
                      </span>
                    </div>
                    <p className="mt-1.5 text-[12.5px] leading-[1.5] text-ink/65">{pt.advice}</p>
                  </div>
                ))}
              </div>
            </Card>
          </Section>
        )}

        {/* Action plan */}
        {Array.isArray(d.action_plan) && d.action_plan.length > 0 && (
          <Section delay={0.26}>
            <Card kicker="Plan d'action" title={t("diagnostic.firstWeeksTitle")} icon={<ListChecks size={16} />}>
              <div className="space-y-3">
                {d.action_plan.map((step, i) => (
                  <div key={i} className="flex gap-3.5">
                    <div className="flex flex-col items-center">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue to-violet font-display text-[13px] font-800 text-white">
                        {i + 1}
                      </span>
                      {i < d.action_plan.length - 1 && (
                        <span className="my-1 w-px flex-1 bg-ink/[0.12]" />
                      )}
                    </div>
                    <div className="flex-1 pb-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-ink/[0.05] px-2 py-0.5 font-mono text-[10px] font-700 uppercase tracking-[0.1em] text-ink/55">
                          {step.timeframe}
                        </span>
                        <span className="font-display text-[14px] font-800 tracking-[-0.01em] text-ink">
                          {step.title}
                        </span>
                      </div>
                      <p className="mt-1 text-[13px] leading-[1.55] text-ink/70">{step.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Section>
        )}

        {/* Inea's note */}
        {d.inea_note && (
          <Section delay={0.32}>
            <div className="flex items-start gap-3.5 rounded-[22px] bg-gradient-to-br from-pink/[0.06] to-violet/[0.06] p-5">
              <p className="flex-1 pt-1 text-[14px] italic leading-[1.6] text-ink/75">
                « {d.inea_note} »
              </p>
            </div>
          </Section>
        )}
      </div>

      <div className="mt-7 flex flex-wrap gap-3">
        <button onClick={() => navigate('/identite')} className="btn-primary">
          <Palette size={16} /> Créer mon identité digitale
        </button>
        <button onClick={() => navigate('/')} className="btn-ghost">
          Mon espace <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function Section({ children, delay }: { children: React.ReactNode; delay: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function Card({
  kicker,
  title,
  icon,
  children,
}: {
  kicker: string;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="card h-full p-6 sm:p-7">
      <div className="mb-4 flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-ink/[0.05] text-ink/65">
          {icon}
        </span>
        <div>
          <p className="font-mono text-[10px] font-700 uppercase tracking-[0.16em] text-ink/45">
            {kicker}
          </p>
          <h3 className="font-display text-[16px] font-800 tracking-[-0.02em]">{title}</h3>
        </div>
      </div>
      {children}
    </section>
  );
}
